import Heading from '@atlaskit/heading';
import { Box, xcss } from '@atlaskit/primitives';
import TruncatedTooltip from '@databases/atlaskit/TruncatedTooltip';
import { useLocale } from '@databases/intl';
import { messages } from '@databases/intl/messages';
import { type DatabaseField, DatabaseFieldType } from '@databases/types/database';
import { toDisplayDate } from '@databases/utils/database/field-types/date';
import React, { type FunctionComponent } from 'react';
import { useIntl } from 'react-intl-next';

import { getExperimentValue } from '@canvas/client-feature-flags';

const titleStyles = xcss({
  display: 'block',
  maxWidth: '100%',
  minWidth: '0',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap'
});

type DatabaseEntryTitleProps = {
  title?: string;
  titleField?: DatabaseField;
  headingLevel: 'h1' | 'h2';
  size?: 'small' | 'medium';
};

const DatabaseEntryTitle: FunctionComponent<DatabaseEntryTitleProps> = ({
  title,
  titleField,
  headingLevel,
  size = 'medium'
}) => {
  const { formatMessage } = useIntl();
  const locale = useLocale();
  const formattedTitle =
    title && titleField?.type === DatabaseFieldType.DATE
      ? (toDisplayDate(
          title,
          locale,
          getExperimentValue('dateFieldSettings', 'enabled', {
            fireExperimentExposure: false
          })
            ? titleField.settings.timeFormat
            : undefined
        ) ?? title)
      : title;
  const resolvedTitle =
    formattedTitle && formattedTitle.trim().length > 0
      ? formattedTitle
      : formatMessage(messages['unnamed-entities.unnamed-entry']);

  return (
    <TruncatedTooltip content={resolvedTitle}>
      <Heading as={headingLevel} size={size} testId="database-entry-title">
        <Box as="span" xcss={titleStyles}>
          {resolvedTitle}
        </Box>
      </Heading>
    </TruncatedTooltip>
  );
};

export default DatabaseEntryTitle;
